/**
 * "Dokument hinzufügen" — pick a file, say where it belongs, name it.
 *
 * The filing is REQUIRED and the save button stays disabled until the whole
 * path is chosen. That is the point of the dialog: Neel's walkthrough is
 * explicit that a file without a filing level makes the archive unsearchable,
 * which is the one thing the Documents page exists to prevent.
 *
 * The path is the Dokumente page's drill-down (COL-628), so a document is
 * found where it was filed (COL-633):
 *
 *   Allgemein · Objektbezogen
 *               └ Objekt: "Elvirastraße, Apartment 1" pills
 *                  └ Wohnungsbezogen · Zimmer ▾   (only while ONE unit is picked)
 *
 * No tenant level and no property level: owners file against a unit or a room.
 * coliviq's own property- and tenant-scoped files still render on the page.
 * Several units of one building can be picked together for a unit-level file;
 * the rules live in `@/lib/owner-document-filing`.
 *
 * Two different writes behind one form:
 *   Objektbezogen → apartment_documents, one row per picked unit (or the one
 *                   room), uploaded once via useUploadDocumentToTargets so
 *                   provenance ('owner') and the storage path cannot drift.
 *   Allgemein     → owner_documents, which is where a file with no property
 *                   can live (20261012085007).
 */
import { useEffect, useMemo, useState } from "react";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { Check, Upload } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Pill, PillMenu, SubRow } from "@/components/owner/OwnerDocumentPills";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import {
  useProperties,
  useOwnerVisiblePropertyStates,
  ownerPropertyIsLive,
} from "@/hooks/v2/useProperties";
import {
  ownerPropertyApartmentsQuery,
  ownerPropertyRoomsQuery,
  useUploadDocumentToTargets,
} from "@/hooks/v2/useApartmentDocuments";
import { ownerPortfolioDocumentsKey } from "@/hooks/owner/useOwnerPortfolioDocuments";
import {
  filingRooms,
  filingTargets,
  filingUnits,
  toggleFilingUnit,
  type FilingApartment,
  type FilingRoom,
  type FilingUnit,
} from "@/lib/owner-document-filing";
import { cn } from "@/lib/utils";

const C = {
  petrol: "#0F4C4A",
  hairline: "#ECE7DD",
  muted: "#7A8E8C",
  body: "#47605E",
  sand: "#FBF9F5",
  tint: "#E3EFED",
} as const;

const DOC_BUCKET = "contracts";

type Top = "general" | "object";

export function OwnerAddDocumentDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const qc = useQueryClient();
  const upload = useUploadDocumentToTargets();

  const [file, setFile] = useState<File | null>(null);
  const [top, setTop] = useState<Top | null>(null);
  /** FilingUnit keys. More than one only inside one building. */
  const [unitKeys, setUnitKeys] = useState<string[]>([]);
  const [roomId, setRoomId] = useState<number | null>(null);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: properties = [] } = useProperties(user?.id, !!user?.id && open);
  const { data: states } = useOwnerVisiblePropertyStates(open);
  // Only live buildings: a property still waiting on its contract has nothing
  // to file against, and offering it would fail at the RPC.
  const live = useMemo(
    () =>
      properties
        .filter((p) => ownerPropertyIsLive(states?.get(Number(p.id))))
        .map((p) => ({ id: Number(p.id), title: p.title ?? null })),
    [properties, states]
  );

  // Every live building's apartments and rooms, so the Objekt row can list
  // each unit by name. Same queries (and cache) as the single-property hooks.
  const loadUnits = open && top === "object";
  const apartments = useQueries({
    queries: live.map((p) => ({
      ...ownerPropertyApartmentsQuery(p.id),
      enabled: loadUnits,
    })),
    combine: (results) => ({
      loading: results.some((q) => q.isLoading),
      failed: results.some((q) => q.isError),
      byProperty: new Map<number, FilingApartment[]>(
        live.map((p, i) => [p.id, results[i]?.data ?? []])
      ),
    }),
  });
  const rooms = useQueries({
    queries: live.map((p) => ({
      ...ownerPropertyRoomsQuery(p.id),
      enabled: loadUnits,
    })),
    combine: (results) => ({
      loading: results.some((q) => q.isLoading),
      failed: results.some((q) => q.isError),
      byProperty: new Map<number, FilingRoom[]>(
        live.map((p, i) => [p.id, results[i]?.data ?? []])
      ),
    }),
  });
  const unitsLoading = loadUnits && (apartments.loading || rooms.loading);
  const unitsFailed = loadUnits && (apartments.failed || rooms.failed);
  // A handful of units at most; rebuilt each render rather than memoised on
  // combine() results, which are new objects every render.
  const units = filingUnits(live, apartments.byProperty, rooms.byProperty);

  const picked = units.filter((u) => unitKeys.includes(u.key));
  const single = picked.length === 1 ? picked[0] : null;
  const unitRooms = single
    ? filingRooms(single, rooms.byProperty.get(single.propertyId) ?? [])
    : [];
  const room = unitRooms.find((r) => r.id === roomId) ?? null;

  // Changing a step clears every step below it: a room picked under one unit
  // must not survive into another unit, or into a multi-unit selection.
  function pickTop(next: Top | null) {
    setTop(next);
    setUnitKeys([]);
    setRoomId(null);
  }
  function toggleUnit(key: string) {
    setUnitKeys((cur) => toggleFilingUnit(cur, key, units));
    setRoomId(null);
  }

  useEffect(() => {
    if (!open) {
      setFile(null);
      pickTop(null);
      setTitle("");
      setError(null);
    }
  }, [open]);

  const target =
    top === "object" ? filingTargets(units, unitKeys, roomId) : null;

  /** Every step answered. The save button reads exactly this. */
  const complete =
    !!file &&
    !!title.trim() &&
    (top === "general" || (top === "object" && target != null));

  function pickFile(next: File | null) {
    setFile(next);
    // Prefilled from the filename, minus the extension, and only while the
    // owner has not typed their own: overwriting a typed title on a re-pick
    // would silently discard it.
    if (next && !title.trim()) {
      setTitle(next.name.replace(/\.[^.]+$/, ""));
    }
  }

  async function save() {
    if (!file || !complete || !user?.id) return;
    setBusy(true);
    setError(null);
    try {
      if (top === "general") {
        const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        const path = `owner-documents/${user.id}/${Date.now()}-${safe}`;
        const up = await supabase.storage
          .from(DOC_BUCKET)
          .upload(path, file, { upsert: false });
        if (up.error) throw up.error;
        const { error: insertError } = await supabase
          .from("owner_documents")
          .insert({
            owner_user_id: user.id,
            title: title.trim(),
            // The only doc_type the owner policy allows — CoLiviq's own
            // categories stay admin-written.
            doc_type: "other",
            file_url: path,
            file_name: file.name,
            file_size: file.size,
            content_type: file.type || null,
            uploaded_by: user.id,
          });
        if (insertError) {
          // Never leave the bytes behind when the row was rejected.
          await supabase.storage.from(DOC_BUCKET).remove([path]);
          throw insertError;
        }
      } else {
        await upload.mutateAsync({
          propertyId: target!.propertyId,
          targets: target!.targets,
          file,
          title: title.trim(),
          source: "owner",
        });
      }
      await qc.invalidateQueries({
        queryKey: ownerPortfolioDocumentsKey(user.id),
      });
      onOpenChange(false);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : t(
              "owner.docs.saveFailed",
              "Das Dokument konnte nicht abgelegt werden."
            )
      );
    } finally {
      setBusy(false);
    }
  }

  const unitLabel = (u: FilingUnit) =>
    [
      u.propertyTitle ?? t("owner.docs.levelProperty", "Immobilie"),
      u.apartmentLabel,
    ]
      .filter(Boolean)
      .join(", ");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px] rounded-[18px]">
        <DialogHeader>
          <DialogTitle style={{ color: C.petrol }}>
            {t("owner.docs.addDocument", "Dokument hinzufügen")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "owner.docs.addDocumentSub",
              "Datei wählen und korrekt ablegen."
            )}
          </DialogDescription>
        </DialogHeader>

        <label
          className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed p-3.5"
          style={{ borderColor: C.hairline, background: C.sand }}
        >
          <span
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
            style={{ background: C.tint, color: C.petrol }}
            aria-hidden
          >
            <Upload className="h-4 w-4" />
          </span>
          <span className="min-w-0 flex-1">
            <span
              className="block truncate text-[13px] font-semibold"
              style={{ color: C.petrol }}
            >
              {file?.name ?? t("owner.docs.chooseFile", "Datei wählen")}
            </span>
            <span className="block text-xs" style={{ color: C.muted }}>
              {t(
                "owner.docs.chooseFileHint",
                "Klicken oder Datei hierher ziehen · PDF, JPG, PNG"
              )}
            </span>
          </span>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
            className="sr-only"
            onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
          />
        </label>

        <fieldset>
          <legend
            className="text-[13px] font-semibold mb-2"
            style={{ color: C.petrol }}
          >
            {t("owner.docs.filing", "Ablage")} *
          </legend>
          <div className="flex flex-wrap gap-1.5">
            <Pill active={top === "general"} onClick={() => pickTop("general")}>
              {t("owner.docs.levelGeneral", "Allgemein")}
            </Pill>
            <Pill active={top === "object"} onClick={() => pickTop("object")}>
              {t("owner.docs.chipObject", "Objektbezogen")}
            </Pill>
          </div>
        </fieldset>

        {top === "object" && (
          <div className="flex flex-col gap-2">
            <div>
              <p
                className="text-[13px] font-semibold mb-2"
                style={{ color: C.petrol }}
              >
                {t("owner.docs.levelObject", "Objekt")}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {units.map((u) => {
                  const on = unitKeys.includes(u.key);
                  return (
                    <Pill
                      key={u.key}
                      active={on}
                      onClick={() => toggleUnit(u.key)}
                    >
                      {on && (
                        <Check className="h-3 w-3 flex-none" aria-hidden />
                      )}
                      <span className="truncate">{unitLabel(u)}</span>
                    </Pill>
                  );
                })}
                {unitsLoading ? (
                  <p className="text-xs" style={{ color: C.muted }}>
                    {t("owner.docs.loading", "Wird geladen…")}
                  </p>
                ) : unitsFailed ? (
                  <p
                    className="text-xs"
                    style={{ color: "#C0714F" }}
                    role="alert"
                  >
                    {t(
                      "owner.docs.unitsLoadFailed",
                      "Die Wohnungen konnten nicht geladen werden. Bitte versuchen Sie es erneut."
                    )}
                  </p>
                ) : (
                  units.length === 0 && (
                    <p className="text-xs" style={{ color: C.muted }}>
                      {t(
                        "owner.docs.noUnitsToFile",
                        "Noch keine Wohnungen, bei denen Sie ablegen können."
                      )}
                    </p>
                  )
                )}
              </div>
            </div>

            {single && (
              <SubRow>
                {single.apartmentId != null && (
                  <Pill
                    soft
                    active={roomId == null}
                    onClick={() => setRoomId(null)}
                  >
                    {t("owner.docs.chipUnitScope", "Wohnungsbezogen")}
                  </Pill>
                )}
                {unitRooms.length > 0 && (
                  <PillMenu
                    soft
                    active={room != null}
                    label={
                      room
                        ? (room.title ?? t("owner.docs.levelRoom", "Zimmer"))
                        : t("owner.docs.levelRoom", "Zimmer")
                    }
                    heading={t("owner.docs.chooseRoom", "Zimmer wählen")}
                  >
                    {unitRooms.map((r) => (
                      <DropdownMenuItem
                        key={r.id}
                        onSelect={() => setRoomId(r.id)}
                        className={cn(r.id === roomId && "font-semibold")}
                      >
                        {r.title ?? t("owner.docs.levelRoom", "Zimmer")}
                      </DropdownMenuItem>
                    ))}
                  </PillMenu>
                )}
              </SubRow>
            )}

            {picked.length > 1 && (
              <p className="text-xs" style={{ color: C.muted }}>
                {t(
                  "owner.docs.multiUnitNote",
                  "Wird bei jeder der {n} Wohnungen abgelegt."
                ).replace("{n}", String(picked.length))}
              </p>
            )}
          </div>
        )}

        <div>
          <label
            htmlFor="owner-doc-title"
            className="mb-1 block text-[13px] font-semibold"
            style={{ color: C.petrol }}
          >
            {t("owner.docs.titleField", "Bezeichnung")}
          </label>
          <input
            id="owner-doc-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("owner.docs.titlePlaceholder", "z. B. Grundriss EG")}
            className="h-11 w-full rounded-xl border px-3.5 text-sm outline-none"
            style={{ borderColor: C.hairline, background: "#FFFFFF" }}
          />
        </div>

        {error && (
          <p className="text-xs" style={{ color: "#C0714F" }} role="alert">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="h-10 rounded-full border px-4 text-[13px] font-medium"
            style={{ borderColor: C.hairline, color: C.body }}
          >
            {t("owner.docs.cancel", "Abbrechen")}
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={!complete || busy}
            className="h-10 rounded-full px-4 text-[13px] font-semibold text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-45"
            style={{ background: C.petrol }}
          >
            {busy
              ? t("owner.docs.saving", "Wird abgelegt…")
              : t("owner.docs.fileDocument", "Dokument ablegen")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
